/* =========================================================
   Affiche TENNIS DE TABLE « La saison » — le classement, le bilan.
   API : SNTTSaison.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, joueur, date, division,
              mode 'classement' → v1..v12 (relevés de points) + m1, m6, m12
                                  (repères d'axe) + matchs, serie
              mode 'bilan'      → l1..l8 (« Libellé|valeur »), points, serie }
     opts : { palette }

   Contrairement à l'index de golf, le classement au tennis de table se lit
   dans le bon sens : plus de points = meilleur joueur. L'axe n'est donc PAS
   inversé ici. L'écart sur la période et l'échelle sont calculés.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie: { label:'Craie',      bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', table:'#1F4E8C', deep:'#12365F' },
    bleu:  { label:'Bleu salle', bg:'#0E2A4A', ink:'#F4F1E8', muted:'#8FA6BF', accent:'#FF6B2C', line:'#1C4470', card:'#15355C', table:'#1F4E8C', deep:'#0A1F38' },
    vert:  { label:'Vert table', bg:'#10362C', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', table:'#1B5E4A', deep:'#0B271F' },
    nuit:  { label:'Nuit',       bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', table:'#123E70', deep:'#082444' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const ent = n => String(Math.round(n));
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  function stats(p, items, y, x0) {
    x0 = x0 || 54;
    const it = items.filter(x => String(x[0] || '').trim());
    if (!it.length) return '';
    const w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /* Au padel le champ libre du bandeau porte le meilleur tableau disputé,
     pas un classement. */
  const LIB_SERIE = { padel: 'Meilleur tableau', badminton: 'Vos séries' };

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const mode = String(data.mode || 'classement').toLowerCase();
    const cx = PW / 2;

    const DEF = { classement: 'MON CLASSEMENT', bilan: 'MA SAISON' };
    const titre = up(data.titre) || DEF[mode] || 'MA SAISON';
    const ts = fit(titre, 690, 74, 0.54, 2, 32);
    const kicker = up(data.kicker) || up(data.division) || 'CHAMPIONNAT PAR ÉQUIPES';
    const sous = [up(data.club), up(data.date)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = '';

    /* Mode « rang » : un classement national se lit à l'ENVERS — passer de la
       8000e à la 3480e place est une progression. L'axe est donc inversé et le
       delta compté en places gagnées, comme l'index de golf. */
    const rang = mode === 'rang';
    if (mode === 'classement' || rang) {
      const pts = [];
      for (let i = 1; i <= 12; i++) { const v = num(data['v' + i]); if (v != null) pts.push(v); }
      if (pts.length >= 2) {
        const mn = Math.min.apply(null, pts), mx = Math.max.apply(null, pts);
        const marge = Math.max((mx - mn) * 0.22, 40);
        const lo = mn - marge, hi = mx + marge;
        const X0 = 116, X1 = PW - MX, Y0 = 800, Y1 = 420;
        const xx = i => X0 + (i / (pts.length - 1)) * (X1 - X0);
        /* Axe dans le bon sens pour des POINTS (gagner fait monter), inversé
           pour un RANG (descendre dans les numéros fait monter la courbe). */
        const yy = v => rang
          ? Y0 - ((hi - v) / (hi - lo)) * (Y0 - Y1)
          : Y0 - ((v - lo) / (hi - lo)) * (Y0 - Y1);
        const pas = (mx - mn) / 3 || 50;
        const mag = Math.pow(10, Math.floor(Math.log(pas) / Math.LN10));
        const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= pas) || mag * 10;
        for (let g = Math.ceil(mn / step) * step; g <= mx + 0.001; g += step) {
          corps += `<line x1="${X0-12}" y1="${yy(g).toFixed(1)}" x2="${X1}" y2="${yy(g).toFixed(1)}" stroke="${p.line}" stroke-width="1.4"/>`
            + T(X0-24, yy(g)+5, 15, p.muted, ent(g), { w:600, a:'end' });
        }
        const co = pts.map((v, i) => `${xx(i).toFixed(1)},${yy(v).toFixed(1)}`);
        corps += `<path d="M ${co[0]} L ${co.slice(1).join(' L ')} L ${X1},${Y0} L ${X0},${Y0} Z" fill="${p.accent}" opacity="0.10"/>`
          + `<path d="M ${co[0]} L ${co.slice(1).join(' L ')}" fill="none" stroke="${p.accent}" stroke-width="5" stroke-linejoin="round" stroke-linecap="round"/>`;
        pts.forEach((v, i) => { corps += `<circle cx="${xx(i).toFixed(1)}" cy="${yy(v).toFixed(1)}" r="5.5" fill="${p.ink}"/>`; });
        const li = pts.length - 1;
        corps += `<circle cx="${xx(li).toFixed(1)}" cy="${yy(pts[li]).toFixed(1)}" r="13" fill="${p.accent}"/><circle cx="${xx(li).toFixed(1)}" cy="${yy(pts[li]).toFixed(1)}" r="5" fill="${p.bg}"/>`;
        [[0, data.m1], [Math.floor(li/2), data.m6], [li, data.m12]].forEach(([i, lab], k) => {
          if (!lab) return;
          corps += T(k === 0 ? X0 : k === 2 ? X1 : xx(i), Y0 + 38, 15, p.muted, esc(up(lab)), { w:600, ls:2, a: k === 0 ? 'start' : k === 2 ? 'end' : 'middle' });
        });
        /* Sur un rang, la progression est le nombre de PLACES gagnées : c'est
           l'écart pris à l'envers. */
        const delta = rang ? pts[0] - pts[li] : pts[li] - pts[0];
        corps += `<text x="${MX}" y="330" font-family="Oswald" font-weight="700" fill="${p.accent}" text-anchor="start"><tspan font-size="86">${delta >= 0 ? '+' : ''}${ent(delta)}</tspan></text>`
          + T(MX, 362, 15, p.muted, rang ? 'PLACES GAGNÉES' : 'POINTS GAGNÉS', { w:600, ls:3, a:'start' })
          + `<text x="${PW-MX}" y="330" font-family="Oswald" font-weight="700" fill="${p.ink}" text-anchor="end"><tspan font-size="86">${ent(pts[li])}</tspan></text>`
          + T(PW-MX, 362, 15, p.muted, rang ? 'MON RANG' : 'AUJOURD’HUI', { w:600, ls:3, a:'end' });
        barre = stats(p, [[ent(pts[0]), 'Départ'], [data.serie, LIB_SERIE[data.sport] || 'Classement'], [data.matchs, 'Matchs']], 916);
      } else {
        corps = T(cx, 600, 21, p.muted, 'SAISISSEZ AU MOINS DEUX RELEVÉS DE POINTS', { w:600, ls:3 });
      }
      pied = data.joueur ? T(cx, 1016, 19, p.ink, esc(up(data.joueur)), { f:'Oswald', w:700, ls:2 }) : '';

    } else { /* bilan */
      const lignes = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
        const [lab, v] = raw.split('|').map(s => (s || '').trim());
        if (lab && v) lignes.push([lab, v]);
      }
      if (lignes.length) {
        /* La dernière ligne est mise en avant : c'est le fait marquant de la
           saison (une montée, un titre), pas une statistique de plus. */
        const zone = 600, pas = Math.min(96, Math.floor(zone / lignes.length));
        const y0 = 348 + (zone - pas * lignes.length) / 2 - 40;
        lignes.forEach((l, i) => {
          const y = y0 + i * pas, dernier = i === lignes.length - 1;
          corps += T(MX, y, 17, p.muted, esc(up(l[0])), { w:600, ls:2, a:'start' })
            + T(PW - MX, y + 8, fit(l[1], 420, 46, 0.58, 0, 22), dernier ? p.accent : p.ink, esc(l[1]), { f:'Oswald', w:700, a:'end' });
          if (!dernier) corps += `<line x1="${MX}" y1="${(y + pas/2 - 14).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 14).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else {
        corps = T(cx, 600, 21, p.muted, 'SAISISSEZ AU MOINS UNE LIGNE, AU FORMAT VICTOIRES 27', { w:600, ls:2 });
      }
      /* esc() OBLIGATOIRE : sans lui, un client qui saisit « R&D 12 » ou
         « <3 pts » produit un SVG XML invalide, et le fichier d'impression
         n'est jamais généré. */
      pied = (data.joueur ? T(cx, 976, 19, p.ink, esc(up(data.joueur)), { f:'Oswald', w:700, ls:2 }) : '')
        + T(cx, 1006, 14, p.muted, esc([up(data.serie), data.points ? up(data.points) + ' POINTS' : ''].filter(Boolean).join(' · ')), { w:600, ls:4 });
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barre}
${pied}
</svg>`;
  }

  window.SNTTSaison = { render, PALETTES };
})();
